import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { UserMedia } from '../../models/user-media';
import { MediaService } from '../../services/media.service';

interface MediaEditState {
  id: string;
  description: string;
  caption: string;
  isEditing: boolean;
  isDeleting: boolean;
}

@Component({
  selector: 'lib-media-update',
  templateUrl: './media-update.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class MediaUpdateComponent implements OnInit {
  @Input() mediaData$: Observable<UserMedia[]> = of([]);
  @Input() currentPage: number = 1;
  @Input() pageSize: number = 12;
  @Input() totalItems: number = 0;
  @Output() saveComplete = new EventEmitter<UserMedia[]>();
  @Output() cancelEdit = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  mediaItems: UserMedia[] = [];
  editStates: MediaEditState[] = [];
  loading = false;
  saveInProgress = false;
  
  // Delete modal state
  showDeleteModal = false;
  mediaToDelete: { media: UserMedia; index: number } | null = null;

  constructor(private mediaService: MediaService) {}

  ngOnInit(): void {
    this.mediaData$.subscribe(items => {
      this.mediaItems = items;
      this.initEditStates();
    });
  }

  private initEditStates(): void {
    this.editStates = this.mediaItems.map(item => ({
      id: item._id,
      description: item.description || '',
      caption: item.caption || '',
      isEditing: false,
      isDeleting: false
    }));
  }

  toggleEdit(index: number): void {
    this.editStates[index].isEditing = !this.editStates[index].isEditing;
    if (this.editStates[index].isEditing) {
      // Reset the form values to the current media values when entering edit mode
      const media = this.mediaItems[index];
      this.editStates[index].description = media.description || '';
      this.editStates[index].caption = media.caption || '';
    }
  }

  get hasEditingState(): boolean {
    return Array.isArray(this.editStates) && this.editStates.some((state: MediaEditState) => !!state && state.isEditing);
  }

  onSave(): void {
    this.saveInProgress = true;
    
    // Filter out items that have been edited and are in edit mode
    const updatesPromises = this.editStates
      .filter((state, index) => state.isEditing)
      .map(state => {
        const updates = {
          description: state.description,
          caption: state.caption
        };
        
        return this.mediaService.updateMediaDetails(state.id, updates).toPromise();
      });
    
    Promise.all(updatesPromises)
      .then(() => {
        // Update the media items with the edited values
        this.mediaItems = this.mediaItems.map((item, idx) => {
          if (this.editStates[idx].isEditing) {
            return {
              ...item,
              description: this.editStates[idx].description,
              caption: this.editStates[idx].caption
            };
          }
          return item;
        });
        
        // Reset edit states
        this.editStates.forEach(state => state.isEditing = false);
        
        // Emit save complete event with updated media items
        this.saveComplete.emit(this.mediaItems);
      })
      .catch(error => {
        console.error('Error updating media details:', error);
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  onCancel(): void {
    // Reset edit states
    this.initEditStates();
    this.cancelEdit.emit();
  }

  // Pagination computed properties
  get totalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  get hasNextPage(): boolean {
    return this.currentPage < this.totalPages;
  }

  get hasPrevPage(): boolean {
    return this.currentPage > 1;
  }

  get paginatedMediaItems(): UserMedia[] {
    // Return all items since pagination is handled by parent component
    return this.mediaItems;
  }

  // Pagination methods
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.pageChange.emit(page);
  }

  goToNextPage(): void {
    if (this.hasNextPage) {
      this.goToPage(this.currentPage + 1);
    }
  }

  goToPrevPage(): void {
    if (this.hasPrevPage) {
      this.goToPage(this.currentPage - 1);
    }
  }

  changePageSize(size: number): void {
    this.pageSizeChange.emit(size);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    
    if (this.totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, and pages around current
      if (this.currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push(-1); // ellipsis
        pages.push(this.totalPages);
      } else if (this.currentPage >= this.totalPages - 2) {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = this.totalPages - 3; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(this.totalPages);
      }
    }
    
    return pages;
  }

  /**
   * Saves a single media item's changes
   * @param index The index of the media item to save
   */
  saveItem(index: number): void {
    const state = this.editStates[index];
    if (!state.isEditing) return;

    this.mediaService.updateMediaDetails(state.id, {
      description: state.description,
      caption: state.caption
    }).subscribe({
      next: () => {
        this.mediaItems[index] = {
          ...this.mediaItems[index],
          description: state.description,
          caption: state.caption
        };
        state.isEditing = false;
        this.saveComplete.emit(this.mediaItems);
      },
      error: (error: unknown) => {
        console.error('Error updating media details:', error);
      }
    });
  }

  /**
   * Shows the delete confirmation modal
   * @param index The index of the media item to delete
   */
  onDelete(index: number): void {
    const media = this.mediaItems[index];
    this.mediaToDelete = { media, index };
    this.showDeleteModal = true;
  }

  /**
   * Closes the delete confirmation modal
   */
  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.mediaToDelete = null;
  }

  /**
   * Confirms and performs the deletion
   * Removes from both Firebase Storage and MongoDB
   */
  confirmDelete(): void {
    if (!this.mediaToDelete) return;
    
    const { media, index } = this.mediaToDelete;
    
    // Set deleting state
    this.editStates[index].isDeleting = true;
    this.showDeleteModal = false;
    
    // Perform deletion
    this.mediaService.deleteMedia(media).subscribe({
      next: () => {
        // Remove from local arrays
        this.mediaItems = this.mediaItems.filter((_, i) => i !== index);
        this.editStates = this.editStates.filter((_, i) => i !== index);
        
        // Reset modal state
        this.mediaToDelete = null;
        
        // Emit updated list
        this.saveComplete.emit(this.mediaItems);
      },
      error: (error) => {
        console.error('Error deleting media:', error);
        this.editStates[index].isDeleting = false;
        this.mediaToDelete = null;
        
        // Could add a toast notification here instead of alert
        alert('Failed to delete media. Please try again.');
      }
    });
  }
}
